import React, { useRef, useState } from 'react';
import { Paperclip, X, Loader2 } from 'lucide-react';
import { Notify, NotifyError } from '../utils/functions';
import {
    ATTACHMENT_ACCEPT,
    formatFileSize,
    MAX_ATTACHMENT_SIZE,
    MAX_ATTACHMENT_TOTAL_SIZE,
    readAttachments,
} from '../utils/support-attachments';

/**
 * File picker for support tickets and replies. Controlled — the parent owns the
 * attachments array and passes it back in, so the total-size cap can account for
 * files already staged.
 *
 * `compact` renders just an icon button, for sitting inside a message composer.
 */
const AttachmentPicker = ({
    attachments = [],
    onChange,
    disabled = false,
    compact = false,
    label = 'Attach files',
}) => {
    const inputRef = useRef(null);
    const [reading, setReading] = useState(false);

    const handleFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        setReading(true);
        try {
            const added = await readAttachments(files, attachments);
            if (added.length) {
                onChange([...attachments, ...added]);
                Notify(`${added.length} file(s) attached`, 'success');
            }
        } catch (error) {
            NotifyError('Failed to read file', error);
        } finally {
            setReading(false);
            // Reset so picking the same file twice in a row still fires onChange.
            event.target.value = '';
        }
    };

    const removeAt = (index) => onChange(attachments.filter((_, i) => i !== index));

    const usedBytes = attachments.reduce((sum, att) => sum + (att.size || 0), 0);

    return (
        <div>
            <input
                ref={inputRef}
                type='file'
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={handleFiles}
                className='hidden'
                disabled={disabled || reading}
            />

            <button
                type='button'
                onClick={() => inputRef.current?.click()}
                disabled={disabled || reading}
                className={compact
                    ? 'flex h-10 w-10 items-center justify-center rounded-xl border border-[#E6DDCF] bg-white text-[#8C8175] transition-colors hover:bg-cream disabled:opacity-60'
                    : 'flex items-center gap-2 rounded-xl border-[1.5px] border-dashed border-[#E6DDCF] bg-white px-4 py-2.5 text-sm font-medium text-[#6B675F] transition-colors hover:border-[#E0C3A4] hover:bg-cream disabled:opacity-60'}
                title={compact ? label : undefined}
            >
                {reading
                    ? <Loader2 className='h-4 w-4 animate-spin' />
                    : <Paperclip className='h-4 w-4' />}
                {!compact && (reading ? 'Reading files...' : label)}
            </button>

            {!compact && (
                <p className='mt-1.5 text-xs text-[#A89C8C]'>
                    PDF, Word, JPEG, PNG or CSV. Up to {formatFileSize(MAX_ATTACHMENT_SIZE)} per file
                    {usedBytes > 0
                        ? `, ${formatFileSize(usedBytes)} of ${formatFileSize(MAX_ATTACHMENT_TOTAL_SIZE)} used.`
                        : '.'}
                </p>
            )}

            {attachments.length > 0 && (
                <div className='mt-3 space-y-2'>
                    {attachments.map((attachment, index) => (
                        <div
                            key={`${attachment.originalName}-${index}`}
                            className='flex items-center justify-between gap-3 rounded-xl border border-[#ECE3D5] bg-white px-3 py-2'
                        >
                            <div className='flex min-w-0 items-center gap-2'>
                                <Paperclip className='h-4 w-4 shrink-0 text-[#A89C8C]' />
                                <span className='truncate text-sm text-[#3B2A1E]'>{attachment.originalName}</span>
                                <span className='shrink-0 text-xs text-[#A89C8C]'>{formatFileSize(attachment.size)}</span>
                            </div>
                            <button
                                type='button'
                                onClick={() => removeAt(index)}
                                disabled={disabled}
                                className='shrink-0 rounded-lg p-1 text-[#A89C8C] transition-colors hover:bg-cream hover:text-red-600 disabled:opacity-60'
                                title='Remove'
                            >
                                <X className='h-4 w-4' />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AttachmentPicker;
